/**
 * Documents query hooks using TanStack Query
 */

import { documentsApi } from '@/lib/api/documents.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { DocumentResponse } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

export interface DocumentFilters {
  search?: string
  moduleType?: string
  moduleId?: string
  categoryId?: string
  type?: string
  isPinned?: boolean
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface DocumentsResponse {
  documents: DocumentResponse[]
  total: number
}

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    totalItems: number
    totalPages: number
    currentPage: number
    pageSize: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export function useDocumentsQuery(
  filters?: DocumentFilters,
  options?: { enabled?: boolean; staleTime?: number },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.documents.list({ ...filters, orgId }),
    queryFn: async (): Promise<PaginatedResponse<DocumentResponse>> => {
      const response: DocumentsResponse = await documentsApi.getDocumentsList({
        search: filters?.search,
        moduleType: filters?.moduleType,
        moduleId: filters?.moduleId,
        categoryId: filters?.categoryId,
        type: filters?.type,
        isPinned: filters?.isPinned,
        page: filters?.page || 1,
        limit: filters?.limit || 50,
        sortBy: filters?.sortBy,
        sortOrder: filters?.sortOrder,
      })

      const documents = response.documents || []
      const total = response.total ?? documents.length
      const page = filters?.page || 1
      const limit = filters?.limit || 50
      const totalPages = Math.ceil(total / limit)

      return {
        data: documents,
        meta: {
          totalItems: total,
          totalPages,
          currentPage: page,
          pageSize: limit,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      }
    },
    enabled: !!orgId && options?.enabled !== false,
    placeholderData: previous => previous,
    staleTime: options?.staleTime ?? 30000,
  })
}

export function useDocumentQuery(
  documentId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.documents.detail(documentId || ''),
    queryFn: () => documentsApi.getDocument(documentId!),
    enabled: !!orgId && !!documentId && options?.enabled !== false,
    staleTime: 30000,
  })
}

export function useDocumentCategoriesQuery(
  params?: { moduleType?: string; moduleId?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.documents.categories({ ...params, orgId }),
    queryFn: () =>
      documentsApi.getCategories(params?.moduleType, params?.moduleId),
    enabled: !!orgId && options?.enabled !== false,
    staleTime: 60000,
  })
}

export function useDocumentModuleCounts(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.documents.counts(),
    queryFn: () => documentsApi.getModuleCounts(),
    enabled: !!orgId && options?.enabled !== false,
    staleTime: 30000,
  })
}

export function useUpdateDocumentMutation(options?: {
  invalidateListsOnSuccess?: boolean
}) {
  const queryClient = useQueryClient()
  const invalidateListsOnSuccess = options?.invalidateListsOnSuccess ?? true

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Record<string, unknown>
    }) => {
      return documentsApi.updateDocument(id, data)
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.documents.lists() })
      await queryClient.cancelQueries({
        queryKey: queryKeys.documents.detail(id),
      })

      const previousListQueries = queryClient.getQueriesData<
        PaginatedResponse<DocumentResponse>
      >({
        queryKey: queryKeys.documents.lists(),
      })
      const previousDetail = queryClient.getQueryData<DocumentResponse>(
        queryKeys.documents.detail(id),
      )

      const optimisticUpdate: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(data)) {
        if (value === undefined) continue
        if (value instanceof Date) {
          optimisticUpdate[key] = value.toISOString()
        } else {
          optimisticUpdate[key] = value
        }
      }

      if (Object.keys(optimisticUpdate).length > 0) {
        const customFieldEntries = Object.entries(optimisticUpdate)
          .filter(([key]) => key.startsWith('custom_'))
          .map(([key, value]) => [key.replace('custom_', ''), value])
        const hasCustomFieldEntries = customFieldEntries.length > 0

        queryClient.setQueriesData<PaginatedResponse<DocumentResponse>>(
          { queryKey: queryKeys.documents.lists() },
          old => {
            if (!old) return old
            return {
              ...old,
              data: old.data.map(doc =>
                doc.id === id
                  ? ({
                      ...doc,
                      ...optimisticUpdate,
                      ...(hasCustomFieldEntries
                        ? {
                            customFields: {
                              ...((
                                doc as DocumentResponse & {
                                  customFields?: Record<string, unknown>
                                }
                              ).customFields || {}),
                              ...Object.fromEntries(customFieldEntries),
                            },
                          }
                        : {}),
                    } as DocumentResponse)
                  : doc,
              ),
            }
          },
        )
        if (previousDetail) {
          queryClient.setQueryData<DocumentResponse>(
            queryKeys.documents.detail(id),
            {
              ...previousDetail,
              ...optimisticUpdate,
              ...(hasCustomFieldEntries
                ? {
                    customFields: {
                      ...((
                        previousDetail as DocumentResponse & {
                          customFields?: Record<string, unknown>
                        }
                      ).customFields || {}),
                      ...Object.fromEntries(customFieldEntries),
                    },
                  }
                : {}),
            } as DocumentResponse,
          )
        }
      }

      return { previousListQueries, previousDetail }
    },
    onError: (_error, { id }, context) => {
      if (context?.previousListQueries) {
        for (const [queryKey, data] of context.previousListQueries) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.documents.detail(id),
          context.previousDetail,
        )
      }
    },
    onSuccess: updatedDocument => {
      queryClient.setQueriesData<PaginatedResponse<DocumentResponse>>(
        { queryKey: queryKeys.documents.lists() },
        old => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map(doc =>
              doc.id === updatedDocument.id
                ? { ...doc, ...updatedDocument }
                : doc,
            ),
          }
        },
      )
      if (invalidateListsOnSuccess) {
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      }
      queryClient.setQueryData(
        queryKeys.documents.detail(updatedDocument.id),
        updatedDocument,
      )
    },
  })
}

/**
 * useDocumentsQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const documentsListQuery = useDocumentsQuery
